import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Droplets, Calendar as CalendarIcon, CheckCircle2, Clock, Sparkles, LogOut,
  Users, Package, Trash2, Eye, Filter, X, Download, Key, Bell, Mail, MessageSquare,
  ToggleLeft, ToggleRight, UserPlus, ShieldCheck, BarChart3, History, TrendingUp, MapPin,
  Route, ExternalLink
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { API, STATUS_CONFIG } from "../lib/constants";
import { LiveClock } from "../components/LiveClock";
import { DeliveriesMap, buildGoogleMapsRouteUrl } from "../components/DeliveriesMap";
import { Logo } from "../components/Logo";

export const AdminDashboard = ({ token, onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDate, setFilterDate] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [exportDateStart, setExportDateStart] = useState("");
  const [exportDateEnd, setExportDateEnd] = useState("");
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({ whatsapp_enabled: true, email_enabled: true });
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [activeTab, setActiveTab] = useState("prenotazioni");

  // Daily delivery route map
  const [showRouteMap, setShowRouteMap] = useState(false);
  const [routeDate, setRouteDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [routeBookings, setRouteBookings] = useState([]);
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);

  // Multi-admin
  const [admins, setAdmins] = useState([]);
  const [showCreateAdminDialog, setShowCreateAdminDialog] = useState(false);
  const [newAdminUsername, setNewAdminUsername] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [isCreatingAdmin, setIsCreatingAdmin] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);

  // Period statistics
  const [periodType, setPeriodType] = useState("giornaliere");
  const [periodStats, setPeriodStats] = useState([]);
  const [isLoadingPeriodStats, setIsLoadingPeriodStats] = useState(false);
  const [mapBookings, setMapBookings] = useState([]);

  // Customers
  const [clienti, setClienti] = useState([]);
  const [isLoadingClienti, setIsLoadingClienti] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [clienteStorico, setClienteStorico] = useState([]);
  const [isLoadingStorico, setIsLoadingStorico] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.append("stato", filterStatus);
      if (filterDate) params.append("data", filterDate);
      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/prenotazioni?${params.toString()}`, { headers }),
        axios.get(`${API}/admin/stats`, { headers })
      ]);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setIsLoading(false);
    }
  }, [filterStatus, filterDate, headers]);

  const fetchNotificationSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/notifications`, { headers });
      setNotificationSettings(res.data);
    } catch {
      /* usa default in caso di errore */
    }
  }, [headers]);

  useEffect(() => {
    fetchData();
    fetchNotificationSettings();
  }, [fetchData, fetchNotificationSettings]);

  const saveNotificationSettings = async () => {
    setIsSavingNotifications(true);
    try {
      await axios.put(`${API}/admin/settings/notifications`, notificationSettings, { headers });
      toast.success("Impostazioni notifiche salvate");
      setShowNotificationSettings(false);
    } catch (error) {
      toast.error("Errore nel salvataggio delle impostazioni");
    } finally {
      setIsSavingNotifications(false);
    }
  };

  // Multi-admin
  const fetchAdmins = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/admins`, { headers });
      setAdmins(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento amministratori");
    }
  }, [headers]);

  const createAdmin = async () => {
    if (newAdminUsername.trim().length < 3) {
      toast.error("Username deve essere almeno 3 caratteri");
      return;
    }
    if (newAdminPassword.length < 6) {
      toast.error("Password deve essere almeno 6 caratteri");
      return;
    }
    setIsCreatingAdmin(true);
    try {
      await axios.post(`${API}/admin/admins`, { username: newAdminUsername.trim(), password: newAdminPassword }, { headers });
      toast.success("Amministratore creato");
      setShowCreateAdminDialog(false);
      setNewAdminUsername("");
      setNewAdminPassword("");
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    } finally {
      setIsCreatingAdmin(false);
    }
  };

  const deleteAdmin = async () => {
    if (!adminToDelete) return;
    try {
      await axios.delete(`${API}/admin/admins/${adminToDelete.id}`, { headers });
      toast.success("Amministratore eliminato");
      setAdminToDelete(null);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  // Period stats
  const fetchPeriodStats = useCallback(async (tipo) => {
    setIsLoadingPeriodStats(true);
    try {
      const res = await axios.get(`${API}/admin/stats/period?periodo=${tipo}`, { headers });
      setPeriodStats(res.data);
      // Load all bookings for the map filtered by period
      const allBookingsRes = await axios.get(`${API}/admin/prenotazioni`, { headers });
      const all = allBookingsRes.data || [];
      const today = new Date();
      let filtered = all;
      if (tipo === "giornaliere") {
        const todayStr = format(today, "yyyy-MM-dd");
        filtered = all.filter((b) => b.data_consegna === todayStr);
      } else if (tipo === "settimanali") {
        const ws = format(startOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd");
        const we = format(endOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd");
        filtered = all.filter((b) => b.data_consegna >= ws && b.data_consegna <= we);
      } else if (tipo === "mensili") {
        const ms = format(startOfMonth(today), "yyyy-MM-dd");
        const me = format(endOfMonth(today), "yyyy-MM-dd");
        filtered = all.filter((b) => b.data_consegna >= ms && b.data_consegna <= me);
      }
      setMapBookings(filtered);
    } catch (error) {
      toast.error("Errore nel caricamento statistiche");
    } finally {
      setIsLoadingPeriodStats(false);
    }
  }, [headers]);

  // Clienti
  const fetchClienti = useCallback(async () => {
    setIsLoadingClienti(true);
    try {
      const res = await axios.get(`${API}/admin/clienti`, { headers });
      setClienti(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento clienti");
    } finally {
      setIsLoadingClienti(false);
    }
  }, [headers]);

  const fetchClienteStorico = async (cliente) => {
    setSelectedCliente(cliente);
    setIsLoadingStorico(true);
    try {
      const params = new URLSearchParams({ nome: cliente.nome });
      if (cliente.telefono) params.append("telefono", cliente.telefono);
      const res = await axios.get(`${API}/admin/clienti/storico?${params.toString()}`, { headers });
      setClienteStorico(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento storico");
    } finally {
      setIsLoadingStorico(false);
    }
  };

  useEffect(() => {
    if (activeTab === "admins") fetchAdmins();
    if (activeTab === "stats") fetchPeriodStats(periodType);
    if (activeTab === "clienti") fetchClienti();
  }, [activeTab, fetchAdmins, fetchClienti, fetchPeriodStats, periodType]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/prenotazioni/${bookingId}`, { stato: newStatus }, { headers });
      toast.success("Stato aggiornato");
      fetchData();
      setSelectedBooking(null);
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const deleteBooking = async () => {
    if (!bookingToDelete) return;
    try {
      await axios.delete(`${API}/admin/prenotazioni/${bookingToDelete}`, { headers });
      toast.success("Prenotazione eliminata");
      setShowDeleteDialog(false);
      setBookingToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const clearFilters = () => { setFilterStatus("all"); setFilterDate(""); };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error("Le password non corrispondono"); return; }
    if (newPassword.length < 6) { toast.error("La password deve essere di almeno 6 caratteri"); return; }
    setIsChangingPassword(true);
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: currentPassword, new_password: newPassword }, { headers });
      toast.success("Password aggiornata con successo");
      setShowPasswordDialog(false);
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel cambio password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Memoize expensive sort for the daily route (avoid re-sorting on every render)
  const routeBookingsSorted = useMemo(
    () => [...routeBookings].sort((a, b) => (a.ora_consegna || "").localeCompare(b.ora_consegna || "")),
    [routeBookings]
  );

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.append("stato", filterStatus);
      if (exportDateStart) params.append("data_inizio", exportDateStart);
      if (exportDateEnd) params.append("data_fine", exportDateEnd);
      const response = await axios.get(`${API}/admin/prenotazioni/export?${params.toString()}`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prenotazioni_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export completato");
      setShowExportDialog(false);
    } catch (error) {
      toast.error("Errore nell'export");
    }
  };

  // Fetch bookings for daily delivery route map (only confermato + in_attesa)
  const openRouteMap = async (date) => {
    setRouteDate(date);
    setShowRouteMap(true);
    setIsLoadingRoute(true);
    try {
      const res = await axios.get(`${API}/admin/prenotazioni?data=${date}`, { headers });
      const filtered = (res.data || []).filter((b) => b.stato !== "annullato");
      setRouteBookings(filtered);
    } catch (error) {
      toast.error("Errore nel caricamento giro consegne");
    } finally {
      setIsLoadingRoute(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 px-4 py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo size={36} />
            <div>
              <span className="font-bold text-xl text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Acqua Vicina
              </span>
              <span className="ml-2 text-sm text-slate-500">Dashboard Admin</span>
            </div>
          </div>
          <LiveClock className="text-slate-600 text-sm" />
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowNotificationSettings(true)} className="rounded-full" data-testid="notification-settings-btn">
              <Bell className="w-4 h-4 mr-2" /> Notifiche
            </Button>
            <Button variant="outline" onClick={() => setShowPasswordDialog(true)} className="rounded-full" data-testid="change-password-btn">
              <Key className="w-4 h-4 mr-2" /> Cambia Password
            </Button>
            <Button variant="outline" onClick={onLogout} className="rounded-full" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 h-auto p-1 rounded-xl flex flex-wrap gap-1" data-testid="admin-tabs">
            <TabsTrigger value="prenotazioni" className="rounded-lg px-4 py-2" data-testid="tab-prenotazioni">
              <Package className="w-4 h-4 mr-2" /> Prenotazioni
            </TabsTrigger>
            <TabsTrigger value="stats" className="rounded-lg px-4 py-2" data-testid="tab-stats">
              <BarChart3 className="w-4 h-4 mr-2" /> Statistiche
            </TabsTrigger>
            <TabsTrigger value="clienti" className="rounded-lg px-4 py-2" data-testid="tab-clienti">
              <Users className="w-4 h-4 mr-2" /> Clienti
            </TabsTrigger>
            <TabsTrigger value="admins" className="rounded-lg px-4 py-2" data-testid="tab-admins">
              <ShieldCheck className="w-4 h-4 mr-2" /> Amministratori
            </TabsTrigger>
          </TabsList>

          {/* Prenotazioni Tab */}
          <TabsContent value="prenotazioni" className="space-y-6">
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8" data-testid="stats-section">
                {[
                  { icon: <Package className="w-4 h-4" />, label: "Totale", color: "text-slate-500", value: stats.totale_prenotazioni, testid: "stat-totale" },
                  { icon: <Clock className="w-4 h-4" />, label: "In Attesa", color: "text-amber-500", value: stats.in_attesa, testid: "stat-attesa" },
                  { icon: <CheckCircle2 className="w-4 h-4" />, label: "Confermate", color: "text-blue-500", value: stats.confermate, testid: "stat-confermate" },
                  { icon: <CheckCircle2 className="w-4 h-4" />, label: "Completate", color: "text-emerald-500", value: stats.completate, testid: "stat-completate" },
                  { icon: <X className="w-4 h-4" />, label: "Annullate", color: "text-red-500", value: stats.annullate, testid: "stat-annullate" },
                  { icon: <CalendarIcon className="w-4 h-4" />, label: "Oggi", color: "text-sky-500", value: stats.prenotazioni_oggi, testid: "stat-oggi" },
                  { icon: <CalendarIcon className="w-4 h-4" />, label: "Domani", color: "text-purple-500", value: stats.prenotazioni_domani, testid: "stat-domani" }
                ].map((s) => (
                  <div key={s.testid} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                    <div className={`flex items-center gap-2 ${s.color} text-sm mb-1`}>
                      {s.icon} {s.label}
                    </div>
                    <p className="text-2xl font-bold text-slate-900" data-testid={s.testid}>{s.value}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-white rounded-2xl p-4 mb-6 border border-slate-100 shadow-sm" data-testid="filters-section">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">Filtri:</span>
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[180px] rounded-xl" data-testid="filter-status-select">
                    <SelectValue placeholder="Stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli stati</SelectItem>
                    <SelectItem value="in_attesa">In Attesa</SelectItem>
                    <SelectItem value="confermato">Confermato</SelectItem>
                    <SelectItem value="completato">Completato</SelectItem>
                    <SelectItem value="annullato">Annullato</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="w-[180px] rounded-xl" data-testid="filter-date-input" />
                {(filterStatus !== "all" || filterDate) && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500" data-testid="clear-filters-btn">
                    <X className="w-4 h-4 mr-1" /> Rimuovi filtri
                  </Button>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <Button variant="outline" onClick={() => openRouteMap(filterDate || format(new Date(), "yyyy-MM-dd"))} className="rounded-full" data-testid="route-map-btn">
                    <Route className="w-4 h-4 mr-2" /> Mappa Giro
                  </Button>
                  <Button variant="outline" onClick={() => setShowExportDialog(true)} className="rounded-full" data-testid="export-csv-btn">
                    <Download className="w-4 h-4 mr-2" /> Esporta CSV
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="bookings-table">
              <div className="px-6 py-4 border-b border-slate-100">
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Prenotazioni ({bookings.length})
                </h2>
              </div>
              {isLoading ? (
                <div className="p-8 text-center">
                  <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="text-slate-500 mt-2">Caricamento...</p>
                </div>
              ) : bookings.length === 0 ? (
                <div className="p-8 text-center" data-testid="no-bookings-message">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Nessuna prenotazione trovata</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cliente</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Indirizzo</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Acqua</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Data/Ora</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Stato</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Azioni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {bookings.map((booking) => (
                        <tr key={booking.id} className="hover:bg-slate-50 transition-colors" data-testid={`booking-row-${booking.id}`}>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-slate-900">{booking.nome}</p>
                              {booking.telefono && <p className="text-sm text-slate-500">{booking.telefono}</p>}
                              {booking.email && <p className="text-xs text-slate-400">{booking.email}</p>}
                            </div>
                          </td>
                          <td className="px-6 py-4"><p className="text-slate-700 text-sm">{booking.indirizzo}</p></td>
                          <td className="px-6 py-4">
                            <Badge variant="outline" className="capitalize">
                              {booking.tipo_acqua === "naturale" ? <><Droplets className="w-3 h-3 mr-1" /> Naturale</> : <><Sparkles className="w-3 h-3 mr-1" /> Gassata</>}
                            </Badge>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-slate-900 font-medium">{booking.data_consegna}</p>
                            <p className="text-sm text-slate-500">{booking.ora_consegna}</p>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_CONFIG[booking.stato]?.color || 'bg-slate-100'}`}>
                              {STATUS_CONFIG[booking.stato]?.label || booking.stato}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="sm" onClick={() => setSelectedBooking(booking)} className="text-slate-600 hover:text-sky-600" data-testid={`view-booking-${booking.id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => { setBookingToDelete(booking.id); setShowDeleteDialog(true); }} className="text-slate-600 hover:text-red-600" data-testid={`delete-booking-${booking.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Statistiche Tab + Mappa */}
          <TabsContent value="stats" className="space-y-6">
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="stats-period-section">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-sky-500" />
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Statistiche per Periodo
                  </h2>
                </div>
                <Select value={periodType} onValueChange={setPeriodType}>
                  <SelectTrigger className="w-[180px] rounded-xl" data-testid="period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="giornaliere">Giornaliere</SelectItem>
                    <SelectItem value="settimanali">Settimanali</SelectItem>
                    <SelectItem value="mensili">Mensili</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {isLoadingPeriodStats ? (
                <div className="p-8 text-center">
                  <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : periodStats.length === 0 ? (
                <div className="p-8 text-center text-slate-500" data-testid="period-stats-empty">
                  Nessun dato disponibile
                </div>
              ) : (
                <div className="overflow-x-auto" data-testid="period-stats-table">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Periodo</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Totale</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Naturale</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Gassata</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Completate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Annullate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Trend</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {periodStats.map((s) => {
                        const max = Math.max(...periodStats.map(x => x.totale), 1);
                        const pct = Math.round((s.totale / max) * 100);
                        return (
                          <tr key={s.periodo} className="hover:bg-slate-50" data-testid={`period-row-${s.periodo}`}>
                            <td className="px-6 py-4 font-medium text-slate-900">{s.periodo}</td>
                            <td className="px-6 py-4 font-bold text-sky-600">{s.totale}</td>
                            <td className="px-6 py-4 text-slate-700">{s.naturale}</td>
                            <td className="px-6 py-4 text-slate-700">{s.gassata}</td>
                            <td className="px-6 py-4 text-emerald-600">{s.completate}</td>
                            <td className="px-6 py-4 text-red-600">{s.annullate}</td>
                            <td className="px-6 py-4 w-48">
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-sky-400 to-sky-600 rounded-full transition-all" style={{ width: `${pct}%` }} />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Mappa distribuzione consegne */}
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="stats-map-section">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-sky-500" />
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Distribuzione Consegne sul Territorio
                </h2>
                <Badge variant="outline" className="ml-2">
                  {periodType === "giornaliere" ? "Oggi" : periodType === "settimanali" ? "Questa settimana" : "Questo mese"}
                </Badge>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Visualizza la distribuzione geografica delle consegne. Clicca su un marker per vedere i dettagli.
                Utilizza queste informazioni per ottimizzare il giro consegne giornaliero.
              </p>
              <DeliveriesMap bookings={mapBookings} height={500} />
            </div>
          </TabsContent>

          {/* Clienti Tab */}
          <TabsContent value="clienti" className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="clienti-section">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-sky-500" />
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Clienti ({clienti.length})
                  </h2>
                </div>
              </div>
              {isLoadingClienti ? (
                <div className="p-8 text-center">
                  <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : clienti.length === 0 ? (
                <div className="p-8 text-center text-slate-500" data-testid="clienti-empty">Nessun cliente registrato</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cliente</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Telefono</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Ordini</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Naturale</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Gassata</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Ultimo Ordine</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Azioni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {clienti.map((c, idx) => (
                        <tr key={`${c.nome}-${c.telefono || idx}`} className="hover:bg-slate-50" data-testid={`cliente-row-${idx}`}>
                          <td className="px-6 py-4 font-medium text-slate-900">{c.nome}</td>
                          <td className="px-6 py-4 text-slate-700">{c.telefono || "-"}</td>
                          <td className="px-6 py-4 font-bold text-sky-600">{c.totale_ordini}</td>
                          <td className="px-6 py-4 text-slate-700">{c.naturale}</td>
                          <td className="px-6 py-4 text-slate-700">{c.gassata}</td>
                          <td className="px-6 py-4 text-slate-700">{c.ultimo_ordine || "-"}</td>
                          <td className="px-6 py-4">
                            <Button variant="ghost" size="sm" onClick={() => fetchClienteStorico(c)} data-testid={`view-cliente-${idx}`} className="text-sky-600">
                              <History className="w-4 h-4 mr-1" /> Storico
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Amministratori Tab */}
          <TabsContent value="admins" className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="admins-section">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-sky-500" />
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Amministratori ({admins.length})
                  </h2>
                </div>
                <Button onClick={() => setShowCreateAdminDialog(true)} className="btn-primary text-white rounded-full" data-testid="create-admin-btn">
                  <UserPlus className="w-4 h-4 mr-2" /> Nuovo Admin
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Username</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Creato il</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {admins.map((a) => (
                      <tr key={a.id} className="hover:bg-slate-50" data-testid={`admin-row-${a.username}`}>
                        <td className="px-6 py-4 font-medium text-slate-900">{a.username}</td>
                        <td className="px-6 py-4 text-slate-500 text-sm">{a.created_at ? a.created_at.split("T")[0] : "-"}</td>
                        <td className="px-6 py-4">
                          <Button variant="ghost" size="sm" onClick={() => setAdminToDelete(a)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-admin-${a.username}`}>
                            <Trash2 className="w-4 h-4 mr-1" /> Elimina
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Booking Detail Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="sm:max-w-md" data-testid="booking-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Dettagli Prenotazione</DialogTitle>
            <DialogDescription>Gestisci lo stato della prenotazione</DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-slate-500">Cliente</p><p className="font-medium">{selectedBooking.nome}</p></div>
                <div><p className="text-sm text-slate-500">Telefono</p><p className="font-medium">{selectedBooking.telefono || "-"}</p></div>
                {selectedBooking.email && (
                  <div className="col-span-2"><p className="text-sm text-slate-500">Email</p><p className="font-medium">{selectedBooking.email}</p></div>
                )}
                <div className="col-span-2"><p className="text-sm text-slate-500">Indirizzo</p><p className="font-medium">{selectedBooking.indirizzo}</p></div>
                <div><p className="text-sm text-slate-500">Tipo Acqua</p><p className="font-medium capitalize">{selectedBooking.tipo_acqua}</p></div>
                <div><p className="text-sm text-slate-500">Data/Ora</p><p className="font-medium">{selectedBooking.data_consegna} - {selectedBooking.ora_consegna}</p></div>
                {selectedBooking.ricorrenza && selectedBooking.ricorrenza !== "nessuna" && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Abbonamento</p>
                    <p className="font-medium capitalize flex items-center gap-2">
                      <Route className="w-4 h-4 text-sky-500" />
                      Ricorrenza {selectedBooking.ricorrenza}
                    </p>
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-2">Cambia Stato</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <Button key={key} variant={selectedBooking.stato === key ? "default" : "outline"} size="sm"
                      onClick={() => updateBookingStatus(selectedBooking.id, key)}
                      className={`justify-start ${selectedBooking.stato === key ? 'btn-primary text-white' : ''}`}
                      data-testid={`status-btn-${key}`}>
                      {config.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Booking Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600" style={{ fontFamily: 'Outfit, sans-serif' }}>Conferma Eliminazione</DialogTitle>
            <DialogDescription>Sei sicuro di voler eliminare questa prenotazione? L&apos;azione non può essere annullata.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} data-testid="cancel-delete-btn">Annulla</Button>
            <Button variant="destructive" onClick={deleteBooking} data-testid="confirm-delete-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Elimina
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Cambia Password</DialogTitle>
            <DialogDescription>Inserisci la password attuale e la nuova password</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-700 font-medium mb-2 block">Password Attuale</Label>
              <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="h-11 rounded-xl" data-testid="current-password-input" />
            </div>
            <div>
              <Label className="text-slate-700 font-medium mb-2 block">Nuova Password</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Almeno 6 caratteri" className="h-11 rounded-xl" data-testid="new-password-input" />
            </div>
            <div>
              <Label className="text-slate-700 font-medium mb-2 block">Conferma Nuova Password</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="h-11 rounded-xl" data-testid="confirm-password-input" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowPasswordDialog(false)} data-testid="cancel-password-btn">Annulla</Button>
              <Button onClick={handleChangePassword} disabled={isChangingPassword} className="btn-primary text-white" data-testid="save-password-btn">
                {isChangingPassword ? (<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />) : (<><Key className="w-4 h-4 mr-2" /> Salva Password</>)}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export CSV Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="sm:max-w-md" data-testid="export-csv-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Esporta Prenotazioni in CSV</DialogTitle>
            <DialogDescription>Seleziona il range di date per l&apos;export. Lascia vuoto per esportare tutto.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-700 font-medium mb-2 block">Data Inizio</Label>
              <Input type="date" value={exportDateStart} onChange={(e) => setExportDateStart(e.target.value)} className="h-11 rounded-xl" data-testid="export-date-start" />
            </div>
            <div>
              <Label className="text-slate-700 font-medium mb-2 block">Data Fine</Label>
              <Input type="date" value={exportDateEnd} onChange={(e) => setExportDateEnd(e.target.value)} className="h-11 rounded-xl" data-testid="export-date-end" />
            </div>
            <p className="text-xs text-slate-500">Lascia vuoto per esportare tutte le prenotazioni.</p>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowExportDialog(false)} data-testid="cancel-export-btn">Annulla</Button>
              <Button onClick={handleExportCSV} className="btn-primary text-white" data-testid="confirm-export-btn">
                <Download className="w-4 h-4 mr-2" /> Esporta CSV
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notification Settings Dialog */}
      <Dialog open={showNotificationSettings} onOpenChange={setShowNotificationSettings}>
        <DialogContent className="sm:max-w-md" data-testid="notification-settings-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Impostazioni Notifiche</DialogTitle>
            <DialogDescription>Attiva o disattiva le notifiche per le nuove prenotazioni</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="font-medium text-slate-800">WhatsApp</p>
                  <p className="text-xs text-slate-500">Notifica via Twilio</p>
                </div>
              </div>
              <button onClick={() => setNotificationSettings({ ...notificationSettings, whatsapp_enabled: !notificationSettings.whatsapp_enabled })} data-testid="toggle-whatsapp">
                {notificationSettings.whatsapp_enabled ? <ToggleRight className="w-10 h-10 text-emerald-500" /> : <ToggleLeft className="w-10 h-10 text-slate-300" />}
              </button>
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-sky-500" />
                <div>
                  <p className="font-medium text-slate-800">Email</p>
                  <p className="text-xs text-slate-500">Gmail SMTP</p>
                </div>
              </div>
              <button onClick={() => setNotificationSettings({ ...notificationSettings, email_enabled: !notificationSettings.email_enabled })} data-testid="toggle-email">
                {notificationSettings.email_enabled ? <ToggleRight className="w-10 h-10 text-sky-500" /> : <ToggleLeft className="w-10 h-10 text-slate-300" />}
              </button>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowNotificationSettings(false)} data-testid="cancel-notifications-btn">Annulla</Button>
              <Button onClick={saveNotificationSettings} disabled={isSavingNotifications} className="btn-primary text-white" data-testid="save-notifications-btn">
                {isSavingNotifications ? (<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />) : (<><Bell className="w-4 h-4 mr-2" /> Salva Impostazioni</>)}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Admin Dialog */}
      <Dialog open={showCreateAdminDialog} onOpenChange={setShowCreateAdminDialog}>
        <DialogContent className="sm:max-w-md" data-testid="create-admin-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Nuovo Amministratore</DialogTitle>
            <DialogDescription>Crea un nuovo account admin. Username minimo 3 caratteri, password minimo 6.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-700 font-medium mb-2 block">Username</Label>
              <Input value={newAdminUsername} onChange={(e) => setNewAdminUsername(e.target.value)} placeholder="nuovo.admin" className="h-11 rounded-xl" data-testid="new-admin-username-input" />
            </div>
            <div>
              <Label className="text-slate-700 font-medium mb-2 block">Password</Label>
              <Input type="password" value={newAdminPassword} onChange={(e) => setNewAdminPassword(e.target.value)} placeholder="Almeno 6 caratteri" className="h-11 rounded-xl" data-testid="new-admin-password-input" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreateAdminDialog(false)} data-testid="cancel-create-admin-btn">Annulla</Button>
              <Button onClick={createAdmin} disabled={isCreatingAdmin} className="btn-primary text-white" data-testid="confirm-create-admin-btn">
                {isCreatingAdmin ? (<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />) : (<><UserPlus className="w-4 h-4 mr-2" /> Crea Admin</>)}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Admin Dialog */}
      <Dialog open={!!adminToDelete} onOpenChange={(open) => !open && setAdminToDelete(null)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-admin-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600" style={{ fontFamily: 'Outfit, sans-serif' }}>Elimina Amministratore</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare l&apos;amministratore <strong>{adminToDelete?.username}</strong>? L&apos;azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setAdminToDelete(null)} data-testid="cancel-delete-admin-btn">Annulla</Button>
            <Button variant="destructive" onClick={deleteAdmin} data-testid="confirm-delete-admin-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Elimina
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mappa Giro Consegne Dialog */}
      <Dialog open={showRouteMap} onOpenChange={setShowRouteMap}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="route-map-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Route className="w-5 h-5 text-sky-500" />
              Giro Consegne del Giorno
            </DialogTitle>
            <DialogDescription>
              Marker numerati per ordine cronologico (orario consegna). Clicca un marker per i dettagli.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="flex flex-wrap items-center gap-3">
              <Label className="text-slate-700 font-medium">Data:</Label>
              <Input
                type="date"
                value={routeDate}
                onChange={(e) => openRouteMap(e.target.value)}
                className="w-[200px] h-10 rounded-xl"
                data-testid="route-date-input"
              />
              <Badge variant="outline" data-testid="route-count-badge">
                {routeBookings.filter((b) => typeof b.lat === "number").length} consegne geolocalizzate
              </Badge>
            </div>

            {isLoadingRoute ? (
              <div className="p-12 text-center">
                <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : (
              <>
                <DeliveriesMap bookings={routeBookings} height={500} numbered showRoute showHeatLegend={false} />

                {/* Lista ordinata + Apri in Google Maps */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-2xl p-4">
                    <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4" /> Ordine Consegne
                    </h4>
                    {routeBookings.length === 0 ? (
                      <p className="text-sm text-slate-500">Nessuna consegna prevista.</p>
                    ) : (
                      <ol className="space-y-2" data-testid="route-list">
                        {routeBookingsSorted.map((b, i) => (
                            <li key={b.id} className="flex items-start gap-3 text-sm">
                              <span className="w-6 h-6 rounded-full bg-sky-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                {i + 1}
                              </span>
                              <div className="flex-1">
                                <p className="font-medium text-slate-800">
                                  {b.ora_consegna} — {b.nome}
                                </p>
                                <p className="text-xs text-slate-500">{b.indirizzo}</p>
                              </div>
                            </li>
                          ))}
                      </ol>
                    )}
                  </div>

                  <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-2xl p-4 border border-sky-100">
                    <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-sky-600" /> Naviga il Giro
                    </h4>
                    <p className="text-sm text-slate-600 mb-4">
                      Apri il giro completo in Google Maps con tutti i waypoint preimpostati per il navigatore.
                    </p>
                    {(() => {
                      const url = buildGoogleMapsRouteUrl(routeBookings);
                      return (
                        <Button
                          asChild={!!url}
                          disabled={!url}
                          className="w-full btn-primary text-white rounded-full h-12"
                          data-testid="open-google-maps-btn"
                        >
                          {url ? (
                            <a href={url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4 mr-2" /> Apri in Google Maps
                            </a>
                          ) : (
                            <span><ExternalLink className="w-4 h-4 mr-2" /> Nessuna consegna geolocalizzata</span>
                          )}
                        </Button>
                      );
                    })()}
                    <p className="text-xs text-slate-500 mt-3">
                      Il giro è ordinato per orario di consegna. Google Maps ottimizzerà i tempi sul tragitto.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cliente Storico Dialog */}
      <Dialog open={!!selectedCliente} onOpenChange={(open) => { if (!open) { setSelectedCliente(null); setClienteStorico([]); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="cliente-storico-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Storico Ordini - {selectedCliente?.nome}</DialogTitle>
            <DialogDescription>
              {selectedCliente?.telefono ? `Telefono: ${selectedCliente.telefono}` : "Nessun telefono associato"}
            </DialogDescription>
          </DialogHeader>
          {isLoadingStorico ? (
            <div className="p-8 text-center"><div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
          ) : clienteStorico.length === 0 ? (
            <div className="p-6 text-center text-slate-500">Nessun ordine trovato</div>
          ) : (
            <div className="space-y-3 mt-2">
              {clienteStorico.map((b) => (
                <div key={b.id} className="border border-slate-200 rounded-xl p-4 hover:bg-slate-50" data-testid={`storico-booking-${b.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <p className="font-medium text-slate-900">{b.data_consegna} - {b.ora_consegna}</p>
                      <p className="text-sm text-slate-600 mt-1">{b.indirizzo}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize">
                        {b.tipo_acqua === "naturale" ? <><Droplets className="w-3 h-3 mr-1" /> Naturale</> : <><Sparkles className="w-3 h-3 mr-1" /> Gassata</>}
                      </Badge>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_CONFIG[b.stato]?.color || 'bg-slate-100'}`}>
                        {STATUS_CONFIG[b.stato]?.label || b.stato}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
